import { Link } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert } from '@fluxora/ui';

/**
 * `/offer/unavailable` (E-SF16) — THE one failure page for an offer link.
 *
 * ── ONE PAGE, ON PURPOSE (US-SF16.2) ───────────────────────────────────────
 * Unknown, expired, revoked, wrong-purpose and malformed all land here, and
 * nothing on this page varies between them. That is not a shortcut: a link that
 * says "this offer expired" and a link that says "no such offer" are two different
 * answers to whoever is holding the URL — and for a link that has been sitting in
 * an inbox, that is not necessarily the customer. The discriminating detail lives
 * in the operator log, where it belongs.
 *
 * ⚠️ DO NOT ADD A REASON PARAMETER. There is no `?reason=` to read and no state to
 * branch on, because the BFF deliberately does not send one — the indistinguishability
 * is a property of the whole path, and a page that COULD render a reason is a page
 * someone will eventually be asked to.
 *
 * ── THE ROUTE FORWARD IS A HUMAN, AND SHOPPING ─────────────────────────────
 * The customer cannot fix this themselves: only the agent can send a fresh offer.
 * So the page offers support, and — because a customer who wanted electricity
 * still wants electricity — a way into the ordinary storefront.
 *
 * `role="status"`, not `role="alert"`: this is an expected outcome of a link that
 * has aged, not a fault. An alert interrupts; a status is announced in turn.
 */
export function OfferUnavailablePage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.offer.unavailable.heading')}</h1>

      <Alert variant="warning" title={t('shop.offer.unavailable.title')} role="status">
        <div className="flex flex-col gap-3">
          <span>{t('shop.offer.unavailable.body')}</span>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/" className="text-sm font-medium text-primary underline">
              {t('shop.offer.unavailable.shopAction')}
            </Link>
            <Link to="/#support" className="text-sm font-medium text-primary underline">
              {t('shop.offer.unavailable.supportAction')}
            </Link>
          </div>
        </div>
      </Alert>
    </main>
  );
}
